import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
    Dialog,
    DialogContent,
} from "@shadcn-in-nx/ui/dialog";
import { X, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";

export function ClickableImage({ src, alt, onImageClick }) {
    return (
        <img
            src={src}
            alt={alt || ""}
            className="max-w-full rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity my-2"
            style={{ maxHeight: "300px", objectFit: "contain" }}
            onClick={(e) => {
                e.stopPropagation();
                onImageClick({ src, alt });
            }}
        />
    );
}

export function ImageGalleryPreview({ images, onImageClick }) {
    if (!images || images.length === 0) return null;
    const first = images[0];
    const remaining = images.length - 1;

    return (
        <div className="relative inline-block">
            <img
                src={first.src}
                alt={first.alt || ""}
                className="max-w-full rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity my-2"
                style={{ maxHeight: "300px", objectFit: "contain" }}
                onClick={(e) => {
                    e.stopPropagation();
                    onImageClick({ images, index: 0 });
                }}
            />
            {remaining > 0 && (
                <button
                    className="absolute bottom-4 right-2 bg-black/70 text-white text-xs font-medium px-2.5 py-1.5 rounded-full cursor-pointer hover:bg-black/85 transition-colors"
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick({ images, index: 0 });
                    }}
                >
                    +{remaining}
                </button>
            )}
        </div>
    );
}

function GalleryLightbox({ image, onClose }) {
    const [currentIndex, setCurrentIndex] = useState(image?.index || 0);
    const images = image.images;
    const total = images.length;
    const current = images[currentIndex];

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowRight" && currentIndex < total - 1) setCurrentIndex((i) => i + 1);
            if (e.key === "ArrowLeft" && currentIndex > 0) setCurrentIndex((i) => i - 1);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [currentIndex, total]);

    return createPortal(
        <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col items-center justify-center" onClick={onClose}>
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={(e) => { e.stopPropagation(); window.open(current?.src, "_blank", "noopener,noreferrer"); }}>
                    <ExternalLink className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={onClose}>
                    <X className="h-4 w-4" />
                </Button>
            </div>
            <img
                src={current?.src}
                alt={current?.alt || ""}
                className="max-w-[85vw] max-h-[75vh] object-contain"
                onClick={(e) => e.stopPropagation()}
            />
            {total > 1 && (
                <div className="flex items-center gap-6 mt-4" onClick={(e) => e.stopPropagation()}>
                    <button
                        className="bg-white/20 hover:bg-white/40 disabled:opacity-30 disabled:hover:bg-white/20 text-white rounded-full h-12 w-12 flex items-center justify-center transition-colors"
                        disabled={currentIndex === 0}
                        onClick={() => setCurrentIndex((i) => i - 1)}
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                    <span className="text-white/70 text-sm min-w-[3rem] text-center">{currentIndex + 1} / {total}</span>
                    <button
                        className="bg-white/20 hover:bg-white/40 disabled:opacity-30 disabled:hover:bg-white/20 text-white rounded-full h-12 w-12 flex items-center justify-center transition-colors"
                        disabled={currentIndex === total - 1}
                        onClick={() => setCurrentIndex((i) => i + 1)}
                    >
                        <ChevronRight className="h-6 w-6" />
                    </button>
                </div>
            )}
        </div>,
        document.body
    );
}

export function ImageLightboxDialog({ image, onClose }) {
    if (!image) return null;

    if (image.images) {
        return <GalleryLightbox image={image} onClose={onClose} />;
    }

    return (
        <Dialog open={!!image} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-black/95 border-none overflow-hidden flex items-center justify-center" hideCloseButton>
                <div className="absolute top-3 right-3 z-10 flex gap-2">
                    <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={() => window.open(image.src, "_blank", "noopener,noreferrer")}>
                        <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>
                <img src={image.src} alt={image.alt || ""} className="max-w-full max-h-[85vh] object-contain" />
            </DialogContent>
        </Dialog>
    );
}
